/**
 * Chrome Speculative Loading / omnibox prerender helpers.
 *
 * WHY THIS EXISTS (do not remove without understanding the demo failure mode):
 * ---------------------------------------------------------------------------
 * Chrome may prerender a same-origin URL while it sits in the address bar
 * *before* the user hits Enter. That speculative document shares `localStorage`
 * with the visible tab, but its `sessionStorage` is isolated until activation
 * (and discarded if the prerender is cancelled).
 *
 * If we apply `/Schedule/?SiteGroup=PEI` during prerender:
 *   - Writing PEI into `selected_facility` flips the *visible* tab's theme
 *     while the user is still looking at e.g. /dashboard (looks broken).
 *   - If the prerender is discarded, session-only "prior SiteGroup" stash is
 *     lost while localStorage stays PEI → cancel/restore has nothing to restore.
 *
 * If we *only* defer and never re-apply after activation:
 *   - React often Navigates `/Schedule/?SiteGroup=PEI` → `/self-reg` during
 *     prerender (query stripped). On Enter the activated page is already
 *     `/self-reg` with no `?SiteGroup=` → branding never switches to PEI.
 *   - Long omnibox waits (prerender) vs quick Enter (cold load) then behave
 *     differently — intermittent demo failures.
 *
 * Contract: never mutate facility/theme localStorage while `document.prerendering`
 * is true; wait for `prerenderingchange` / `waitUntilNotPrerendering()` then apply.
 * Same pattern as Parking Lot Mobile (`MobileClinicDay`).
 */

/** Resolves when the document is not prerendering (or immediately if already active). */
export function waitUntilNotPrerendering(): Promise<void> {
  if (typeof document === 'undefined' || !document.prerendering) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    document.addEventListener('prerenderingchange', () => resolve(), { once: true });
  });
}

/** True while Chrome is prerendering this document (omnibox speculation). */
export function isDocumentPrerendering(): boolean {
  return typeof document !== 'undefined' && !!document.prerendering;
}
